import { useState, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import {
  InputAdornment,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Box,
  FormControl,
  Select,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';

import { getAllRoleAssignments } from './functions/get_all_role_assignments.function';
import { deleteRoleAssignment } from './functions/delete_role_assignment.function';
import {
  buildRoleByIdMap,
  formatRoleLevelDisplay,
  formatRoleLevelFromRoleId,
} from '../RoleAssignment/functions/format_role_level_display.function';

export const RoleAssignmentTable = forwardRef(
  (
    {
      onEdit,
      onDeleteSuccess,
      submitForApproval = false,
      users = [],
      roles = [],
      roleAssignments: rawRoleAssignments = [],
    },
    ref
  ) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRegion, setSelectedRegion] = useState('');
    const [enrichedRoleAssignments, setEnrichedRoleAssignments] = useState([]);
    const [loading, setLoading] = useState(false);

    // Define regions
    const regions = useMemo(() => ['North', 'South', 'Central B', 'Central A'], []);

    // Function to enrich role assignment data with user + typed Planning level labels
    const enrichRoleAssignments = (assignments, usersList, rolesList = roles) => {
      const roleById = buildRoleByIdMap(rolesList);
      return assignments.map((assignment) => {
        const user = usersList.find((u) => u.id === assignment.user_id);
        return {
          ...assignment,
          user_name: user?.name || 'N/A',
          email: user?.email || 'N/A',
          region: user?.region || 'N/A',
          role_name: formatRoleLevelFromRoleId(assignment.role_name, assignment.role_id, roleById),
        };
      });
    };

    // Function to fetch role assignments data (for refetch after add/update/delete)
    const fetchRoleAssignments = async () => {
      setLoading(true);
      try {
        const result = await getAllRoleAssignments();
        const responseArray = result?.response || result?.data || [];
        // Enrich role assignments with user data if users are available
        if (users.length > 0) {
          const enrichedAssignments = enrichRoleAssignments(responseArray, users, roles);
          setEnrichedRoleAssignments(enrichedAssignments);
        } else {
          setEnrichedRoleAssignments(responseArray);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    // Enrich role assignments when users and/or assignments change.
    // Important: clear loading when users are loaded but assignments are still empty (API order),
    // otherwise the table spinner never stops (e.g. System Invoice Approvers tab).
    useEffect(() => {
      if (rawRoleAssignments.length > 0 && users.length > 0) {
        const enriched = enrichRoleAssignments(rawRoleAssignments, users, roles);
        setEnrichedRoleAssignments(enriched);
        setLoading(false);
      } else if (rawRoleAssignments.length > 0 && users.length === 0) {
        setLoading(true);
      } else if (users.length > 0 && rawRoleAssignments.length === 0) {
        setEnrichedRoleAssignments([]);
        setLoading(false);
      } else if (rawRoleAssignments.length === 0 && users.length === 0) {
        setEnrichedRoleAssignments([]);
        setLoading(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users, rawRoleAssignments, roles]);

    // Function to add or update role assignment in the table
    const addOrUpdateRoleAssignment = (assignmentData, usersList, rolesList) => {
      // Find user and role data
      const user = usersList.find((u) => u.id === assignmentData.user_id);
      const role = rolesList.find((r) => r.id === assignmentData.role_id);

      // Create enriched role assignment object
      const enrichedAssignment = {
        id: assignmentData.id,
        user_id: assignmentData.user_id,
        role_id: assignmentData.role_id,
        user_name: user?.name || 'N/A',
        email: user?.email || 'N/A',
        region: user?.region || 'N/A',
        role_name: formatRoleLevelDisplay(
          role?.name || assignmentData.role_name,
          role?.invoice_type
        ),
        assigned_at: assignmentData.assigned_at,
        assigned_by: assignmentData.assigned_by,
      };

      setEnrichedRoleAssignments((prev) => {
        // Check if assignment already exists (for update)
        const existingIndex = prev.findIndex((item) => item.id === enrichedAssignment.id);
        if (existingIndex >= 0) {
          // Update existing assignment
          const updated = [...prev];
          updated[existingIndex] = enrichedAssignment;
          return updated;
        } else {
          // Add new assignment
          return [...prev, enrichedAssignment];
        }
      });
    };

    // Function to remove role assignment from the table
    const removeRoleAssignment = (id) => {
      setEnrichedRoleAssignments((prev) => prev.filter((item) => item.id !== id));
    };

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      refetchRoleAssignments: fetchRoleAssignments,
      addOrUpdateRoleAssignment,
      removeRoleAssignment,
    }));

    const filteredRoleAssignments = useMemo(() => {
      let filtered = enrichedRoleAssignments;

      // Filter by region
      if (selectedRegion) {
        filtered = filtered.filter((assignment) => assignment.region === selectedRegion);
      }

      // Filter by search term
      if (searchTerm) {
        filtered = filtered.filter((assignment) =>
          Object.values(assignment).some((val) =>
            String(val).toLowerCase().includes(searchTerm.toLowerCase())
          )
        );
      }

      return filtered;
    }, [selectedRegion, searchTerm, enrichedRoleAssignments]);

    // eslint-disable-next-line react/prop-types
    const ActionsMenu = ({ row }) => {
      const [anchorEl, setAnchorEl] = useState(null);
      const [deleting, setDeleting] = useState(false);
      const open = Boolean(anchorEl);

      const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
      };

      const handleClose = () => {
        setAnchorEl(null);
      };

      const handleDelete = async () => {
        try {
          setDeleting(true);
          const result = await deleteRoleAssignment(row.original.id, {
            forApproval: submitForApproval,
          });
          if (result.success) {
            if (!submitForApproval) {
              removeRoleAssignment(row.original.id);
            }
            if (onDeleteSuccess) {
              onDeleteSuccess();
            }
          }
        } catch (err) {
          console.error('Delete failed:', err);
        } finally {
          setDeleting(false);
          handleClose();
        }
      };

      return (
        <>
          <IconButton onClick={handleClick} disabled={deleting}>
            <MoreVertIcon sx={{ fontWeight: 300, fontSize: '18px', color: '#667085' }} />
          </IconButton>
          <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
            <MenuItem
              onClick={() => {
                // eslint-disable-next-line react/prop-types
                onEdit(row.original);
                handleClose();
              }}
              disabled={deleting}
            >
              Edit
            </MenuItem>
            <MenuItem onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Submitting...' : submitForApproval ? 'Request removal' : 'Delete'}
            </MenuItem>
          </Menu>
        </>
      );
    };

    const columns = useMemo(
      () => [
        {
          accessorKey: 'role_name',
          header: 'Levels',
          size: 220,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const roleName = cell.getValue();
            return <span style={{ fontSize: '14px', color: '#1D242E' }}>{roleName || 'N/A'}</span>;
          },
        },
        {
          accessorKey: 'user_name',
          header: 'Name',
          size: 150,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const userName = cell.getValue();
            return <span style={{ fontSize: '14px', color: '#1D242E' }}>{userName || 'N/A'}</span>;
          },
        },
        {
          accessorKey: 'region',
          header: 'Region',
          size: 150,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const region = cell.getValue();
            return <span style={{ fontSize: '14px', color: '#667085' }}>{region || 'N/A'}</span>;
          },
        },
        {
          accessorKey: 'email',
          header: 'Email',
          size: 250,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const email = cell.getValue();
            return <span style={{ fontSize: '14px', color: '#667085' }}>{email || 'N/A'}</span>;
          },
        },
        {
          accessorKey: 'assigned_by',
          header: 'Assigned By',
          size: 150,
          enableColumnActions: false,
          // eslint-disable-next-line react/prop-types
          Cell: ({ cell }) => {
            // eslint-disable-next-line react/prop-types
            const assignedBy = cell.getValue();
            return (
              <span style={{ fontSize: '14px', color: '#667085' }}>{assignedBy || 'N/A'}</span>
            );
          },
        },
        {
          header: 'Actions',
          enableColumnActions: false,
          size: 50,
          // eslint-disable-next-line react/prop-types
          Cell: ({ row }) => <ActionsMenu row={row} />,
        },
      ],
      []
    );

    const table = useMaterialReactTable({
      columns,
      data: filteredRoleAssignments,
      state: { isLoading: loading },
      muiCircularProgressProps: {
        color: 'inherit',
        size: 40,
        sx: { color: '#999999' },
      },
      enableGlobalFilter: false,
      enableGlobalFilterModes: false,
      positionGlobalFilter: 'none',
      enableColumnFilterModes: false,
      enableColumnOrdering: false,
      enableGrouping: false,
      enableRowSelection: false,
      enableSelectAll: false,
      enableColumnPinning: false,
      enableFacetedValues: false,
      enableSorting: false,
      enableDensityToggle: false,
      paginationDisplayMode: 'pages',
      positionToolbarAlertBanner: 'bottom',
      initialState: {
        pagination: {
          pageSize: 10,
        },
        columnOrder: ['role_name', 'user_name', 'region', 'email', 'assigned_by', 'actions'],
      },
      muiSearchTextFieldProps: {
        size: 'small',
        variant: 'outlined',
      },
      muiPaginationProps: {
        color: 'secondary',
        rowsPerPageOptions: [10, 20, 30, 40],
        shape: 'rounded',
        variant: 'outlined',
        sx: {
          '& .MuiPaginationItem-root.Mui-selected': {
            backgroundColor: '#BD1D23',
            color: 'white',
          },
        },
      },
      enableBottomToolbar: true,
      enableStickyHeader: true,
      enableStickyFooter: true,
      enablePagination: true,
      muiTableBodyRowProps: {
        sx: {
          height: 52,
          minHeight: 52,
          maxHeight: 52,
        },
      },
      muiTableBodyCellProps: {
        sx: {
          color: '#667085',
          backgroundColor: '#fff',
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '125%',
          padding: '0 12px',
          '&:first-of-type': {
            paddingLeft: '12px !important',
          },
        },
      },
      muiTableHeadCellProps: {
        sx: {
          color: '#344054',
          backgroundColor: '#E8E9E9',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: '600',
          lineHeight: '125%',
          height: '56px',
          padding: '0 12px',
          alignItems: 'center',
          '&:first-of-type': {
            paddingLeft: '18px !important',
          },
        },
      },
      muiTablePaperProps: {
        sx: {
          boxShadow: 'none',
        },
      },
      muiTableHeadRowProps: {
        sx: {
          height: 56,
          '& .MuiTableCell-root': {
            padding: '6px 12px',
            verticalAlign: 'middle',
          },
        },
      },
      renderTopToolbarCustomActions: () => (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          {/* Left side - Search field */}
          <TextField
            placeholder="Search"
            size="small"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoComplete="off"
            sx={{
              backgroundColor: '#fff',
              width: '300px',
              '& .MuiInputBase-input': {
                color: 'gray',
                '::placeholder': {
                  color: '#b3b9c4',
                  opacity: 1,
                },
              },
              '& .MuiOutlinedInput-root': {
                border: '1px solid #ccc',
                borderRadius: '10px',
                paddingRight: 0,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: '#999' }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Right side - Region dropdown */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Region Dropdown */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
                sx={{
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                  color: '#757575',
                  borderRadius: '10px',
                  '& .MuiSelect-select': {
                    padding: '8px 12px',
                  },
                  '&:hover': {
                    border: '1px solid #ccc',
                  },
                  '&.Mui-focused': {
                    border: '1px solid #ccc',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
              >
                <MenuItem
                  value=""
                  sx={{
                    borderBottom: '1px solid #eee',
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                  }}
                >
                  REGIONS
                </MenuItem>
                {regions.map((region, index) => (
                  <MenuItem
                    key={region}
                    value={region}
                    sx={{
                      fontSize: '13px',
                      color: '#656e7a',
                      lineHeight: 1.4,
                      borderBottom: index !== regions.length - 1 ? '1px solid #eee' : 'none',
                    }}
                  >
                    {region}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      ),
    });

    return (
      <div className="role-assignment-table-main-container">
        <div className="role-assignment-table-container">
          <div className="role-assignment-table-wrapper">
            <MaterialReactTable table={table} />
          </div>
        </div>
      </div>
    );
  }
);

RoleAssignmentTable.displayName = 'RoleAssignmentTable';

RoleAssignmentTable.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onDeleteSuccess: PropTypes.func,
  users: PropTypes.array,
  roleAssignments: PropTypes.array,
};
